/**
 * Operator-chrome probe against GET /admin/security/me.
 *
 * Chrome only: success does not authorize /admin APIs. Terminal 200/403 are
 * cached by the provider; 401 and transient failures fail closed (hide Admin).
 */

const DEFAULT_BACKOFF_MS = 200;

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * @param {() => Promise<{ status: number, data?: object|null }>} fetchStatus
 * @param {{ maxTransientRetries?: number, sleep?: (ms: number) => Promise<void>, backoffMs?: number }} [opts]
 * @returns {Promise<{ allowed: boolean, status: object|null, terminal: boolean }>}
 */
export async function probePrivilegedChrome(fetchStatus, opts = {}) {
  const maxTransientRetries = opts.maxTransientRetries ?? 2;
  const sleep = opts.sleep ?? delay;
  const backoffMs = opts.backoffMs ?? DEFAULT_BACKOFF_MS;

  let transientAttempt = 0;
  while (true) {
    try {
      const result = await fetchStatus();
      const code = Number(result?.status);
      if (code === 200) {
        return { allowed: true, status: result.data ?? null, terminal: true, authExpired: false };
      }
      if (code === 403) {
        return { allowed: false, status: null, terminal: true, authExpired: false };
      }
      if (code === 401) {
        return { allowed: false, status: null, terminal: false, authExpired: true };
      }
      // 429 / 5xx / other non-terminal HTTP
      if (transientAttempt >= maxTransientRetries) {
        return { allowed: false, status: null, terminal: false, authExpired: false };
      }
      transientAttempt += 1;
      await sleep(backoffMs * transientAttempt);
    } catch {
      // Network / transport failure — same limited retry then fail closed.
      if (transientAttempt >= maxTransientRetries) {
        return { allowed: false, status: null, terminal: false, authExpired: false };
      }
      transientAttempt += 1;
      await sleep(backoffMs * transientAttempt);
    }
  }
}

/** Axios-backed fetchStatus for the shared provider. */
export async function fetchAdminSecurityMe(apiClient) {
  try {
    const response = await apiClient.get('/admin/security/me');
    return { status: response.status || 200, data: response.data };
  } catch (err) {
    const status = err?.response?.status;
    if (status) return { status, data: null };
    throw err;
  }
}
